import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { getWebhooks, createWebhook, updateWebhook, deleteWebhook } from "src/repos/webhooks";
import { manage } from "src/repos/base";
import { useTranslation } from "react-i18next";
import AddWebhook from "./AddWebhook";

const useStyles = makeStyles((theme) => ({
  hover: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
}));

function Webhooks() {
  const classes = useStyles();
  const { t } = useTranslation(["glossary", "common"]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhooks, setWebhooks] = useState([]);

  const fetchWebhooks = () => {
    getWebhooks()
      .then((r) => r.json())
      .then(({ data }) => setWebhooks(data));
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleCreate = (webhook) => {
    manage(createWebhook(webhook)).on("ok", () => {
      fetchWebhooks();
    });
  };

  const handleUpdate = (webhook) => {
    manage(updateWebhook(webhook.id, webhook)).on("ok", () => {
      fetchWebhooks();
    });
  };

  const handleDelete = (webhook) => {
    manage(deleteWebhook(webhook.id)).on("ok", () => {
      fetchWebhooks();
    });
  };

  const filterByPagination = (items) => {
    const start = page * pageSize;
    return items.slice(start, start + pageSize);
  };

  return (
    <Card>
      <CardHeader
        title={t("webhooks")}
        action={
          <IconButton
            aria-label={t("add")}
            onClick={() => {
              setSelectedWebhook(null);
              setOpen(true);
            }}
            style={{ marginTop: -5, marginBottom: -15 }}
          >
            <AddCircleIcon />
          </IconButton>
        }
      />
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("name")}</TableCell>
              <TableCell>{t("customer")}</TableCell>  {/* NEW */}
              <TableCell>{t("url")}</TableCell>
              <TableCell>{t("auth_type")}</TableCell>
              <TableCell align="center">{t("active")}</TableCell>
              <TableCell align="center">{t("last_called")}</TableCell>   {/* changed from last_successful_call */}
              <TableCell align="center">{t("last_return_code")}</TableCell>  {/* NEW */}
            </TableRow>
          </TableHead>
          <TableBody>
            {filterByPagination(webhooks).map((webhook) => (
              <TableRow
                key={webhook.id}
                hover
                className={classes.hover}
                onClick={() => {
                  setSelectedWebhook(webhook);
                  setOpen(true);
                }}
              >
                <TableCell>{webhook.webhook_name}</TableCell>
                <TableCell>{webhook.customer?.name || "—"}</TableCell>   {/* NEW */}
                <TableCell>{webhook.url}</TableCell>
                <TableCell>{webhook.auth_type || "-"}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={webhook.active ? t("active") : t("inactive")}
                    className={
                      webhook.active ? classes.activeChip : classes.inactiveChip
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {webhook.last_called
                    ? new Date(webhook.last_called).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell align="center">
                  {webhook.last_return_code !== null && webhook.last_return_code !== undefined
                    ? webhook.last_return_code
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 15, 20]}
        component="div"
        count={webhooks.length}
        labelRowsPerPage={t("rows_per_page")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} ${t("of")} ${count !== -1 ? count : t("more_than") + ` ${to}`}`
        }
        rowsPerPage={pageSize}
        page={page}
        onChangePage={(e, newPage) => setPage(newPage)}
        onChangeRowsPerPage={(e) => setPageSize(parseInt(e.target.value, 10))}
      />
      {open && (
        <AddWebhook
          open={open}
          webhook={selectedWebhook}
          onClose={() => {
            setSelectedWebhook(null);
            setOpen(false);
          }}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </Card>
  );
}

export default Webhooks;